import { type Viewport, useStoreApi } from '@xyflow/react'
import {
  type CSSProperties,
  type RefCallback,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { getCssZoomFactor } from '@flowx/shared/utils'

const DEFAULT_CSS_ZOOM = 1

export interface CssZoomCompensation {
  cssZoom: number
  reactFlowRef: RefCallback<HTMLDivElement>
  reactFlowStyle: CSSProperties
  toLogicalViewport: (viewport: Viewport) => Viewport
}

export function normalizeCssZoom(zoom: number): number {
  return Number.isFinite(zoom) && zoom > 0 ? zoom : DEFAULT_CSS_ZOOM
}

export function scaleViewportForCssZoom(
  viewport: Viewport,
  previousCssZoom: number,
  nextCssZoom: number
): Viewport {
  const ratio = normalizeCssZoom(nextCssZoom) / normalizeCssZoom(previousCssZoom)

  return {
    x: viewport.x * ratio,
    y: viewport.y * ratio,
    zoom: viewport.zoom * ratio,
  }
}

export function removeCssZoomFromPixels(value: number, cssZoom: number): number {
  return value / normalizeCssZoom(cssZoom)
}

export function removeCssZoomFromViewport(viewport: Viewport, cssZoom: number): Viewport {
  return {
    x: removeCssZoomFromPixels(viewport.x, cssZoom),
    y: removeCssZoomFromPixels(viewport.y, cssZoom),
    zoom: removeCssZoomFromPixels(viewport.zoom, cssZoom),
  }
}

export function compensatedCanvasSize(
  width: number,
  height: number,
  cssZoom: number
): { width: number; height: number } {
  const scale = normalizeCssZoom(cssZoom)

  return { width: width * scale, height: height * scale }
}

/**
 * React Flow interprets PointerEvent.clientX/clientY in physical viewport pixels,
 * but its viewport transform and canvas dimensions normally live in CSS layout
 * pixels. An ancestor `zoom` (the Command Center PiP uses 0.6) makes those units
 * diverge, so dragged nodes and connection handles lag behind the pointer.
 *
 * Keep React Flow's coordinate system in physical pixels while it is inside CSS
 * zoom. The renderer is counter-scaled in CSS, preserving the existing PiP
 * appearance, while viewport transforms and dimensions are scaled here so all
 * built-in and custom pointer interactions use the same units.
 */
export function useCssZoomCompensation(minZoom: number, maxZoom: number): CssZoomCompensation {
  const flowStoreApi = useStoreApi()
  const appliedCssZoom = useRef(DEFAULT_CSS_ZOOM)
  const currentCssZoom = useRef(DEFAULT_CSS_ZOOM)
  const detectionFrame = useRef<number | null>(null)
  const retryFrame = useRef<number | null>(null)
  const [rootElement, setRootElement] = useState<HTMLDivElement | null>(null)
  const [cssZoom, setCssZoom] = useState(DEFAULT_CSS_ZOOM)
  const [panZoomRetry, setPanZoomRetry] = useState(0)

  const syncCanvasDimensions = useCallback(
    (scale: number) => {
      if (!rootElement) {
        return
      }
      const expected = compensatedCanvasSize(
        rootElement.offsetWidth,
        rootElement.offsetHeight,
        scale
      )
      const state = flowStoreApi.getState()
      if (state.width !== expected.width || state.height !== expected.height) {
        flowStoreApi.setState(expected)
      }
    },
    [flowStoreApi, rootElement]
  )

  const detectCssZoom = useCallback(() => {
    if (!rootElement) {
      return
    }
    const nextCssZoom = normalizeCssZoom(getCssZoomFactor(rootElement))
    currentCssZoom.current = nextCssZoom
    syncCanvasDimensions(nextCssZoom)
    setCssZoom((current) => (current === nextCssZoom ? current : nextCssZoom))
  }, [rootElement, syncCanvasDimensions])

  const scheduleDetection = useCallback(() => {
    if (detectionFrame.current !== null) {
      cancelAnimationFrame(detectionFrame.current)
    }
    detectionFrame.current = requestAnimationFrame(() => {
      detectionFrame.current = null
      detectCssZoom()
    })
  }, [detectCssZoom])

  const reactFlowRef = useCallback<RefCallback<HTMLDivElement>>((element) => {
    setRootElement(element)
  }, [])

  const toLogicalViewport = useCallback(
    (viewport: Viewport) => removeCssZoomFromViewport(viewport, currentCssZoom.current),
    []
  )

  useLayoutEffect(() => {
    const previousCssZoom = appliedCssZoom.current
    if (cssZoom === previousCssZoom) {
      syncCanvasDimensions(cssZoom)

      return
    }

    const state = flowStoreApi.getState()
    if (!state.panZoom) {
      retryFrame.current = requestAnimationFrame(() => {
        retryFrame.current = null
        setPanZoomRetry((retry) => retry + 1)
      })

      return
    }

    // Widen before raising the minimum; lower the minimum before narrowing the
    // maximum, so the transient scale extent is always valid in both directions.
    if (cssZoom > previousCssZoom) {
      state.setMaxZoom(maxZoom * cssZoom)
      state.setMinZoom(minZoom * cssZoom)
    } else {
      state.setMinZoom(minZoom * cssZoom)
      state.setMaxZoom(maxZoom * cssZoom)
    }

    appliedCssZoom.current = cssZoom
    syncCanvasDimensions(cssZoom)
    void state.panZoom.setViewport(
      scaleViewportForCssZoom(
        { x: state.transform[0], y: state.transform[1], zoom: state.transform[2] },
        previousCssZoom,
        cssZoom
      )
    )
  }, [cssZoom, flowStoreApi, maxZoom, minZoom, panZoomRetry, syncCanvasDimensions])

  useLayoutEffect(() => {
    if (!rootElement) {
      return undefined
    }

    const resizeObserver = new ResizeObserver(scheduleDetection)
    resizeObserver.observe(rootElement)
    const bodyClassObserver = new MutationObserver(scheduleDetection)
    bodyClassObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] })
    window.addEventListener('resize', scheduleDetection)
    detectCssZoom()

    return () => {
      resizeObserver.disconnect()
      bodyClassObserver.disconnect()
      window.removeEventListener('resize', scheduleDetection)
      if (detectionFrame.current !== null) {
        cancelAnimationFrame(detectionFrame.current)
      }
      if (retryFrame.current !== null) {
        cancelAnimationFrame(retryFrame.current)
      }
    }
  }, [detectCssZoom, rootElement, scheduleDetection])

  return {
    cssZoom,
    reactFlowRef,
    reactFlowStyle: {
      '--bpmn-react-flow-compensated-size': `${cssZoom * 100}%`,
      '--bpmn-react-flow-inverse-css-zoom': 1 / cssZoom,
    } as CSSProperties,
    toLogicalViewport,
  }
}
